import { computed, Ref, ref } from '@vue/composition-api'

type State = string | number | boolean | undefined

interface Toggle<T extends State = boolean> {
  (value?: T): T
  toggle: (value?: T) => T
  setDefault: () => T
  setReverse: () => T
  isDefault: Readonly<Ref<boolean>>
  isReverse: Readonly<Ref<boolean>>
}

export const useToggle = <T extends State = boolean, U extends State = boolean>(
  defaultValue: T = false as boolean & T,
  reverseValue: U = !defaultValue as boolean & U
): [Ref<T | U>, Toggle<T | U>] => {
  const state = ref<T | U>(defaultValue) as Ref<T | U>
  const realReverseValue =
    reverseValue === undefined ? (!defaultValue as T) : reverseValue

  const oppositeValue = computed<T | U>(() =>
    state.value === defaultValue ? realReverseValue : defaultValue
  )

  state.value = defaultValue

  const toggle: Toggle<T | U> = (value?: T | U) =>
    (state.value = typeof value === 'undefined' ? oppositeValue.value : value)

  const setDefault = () => (state.value = defaultValue)
  const setReverse = () => (state.value = realReverseValue)

  toggle.toggle = value => toggle(value)
  toggle.setDefault = setDefault
  toggle.setReverse = setReverse
  toggle.isDefault = computed(() => state.value === defaultValue)
  toggle.isReverse = computed(() => state.value === realReverseValue)

  return [state, toggle]
}
